import { useLayoutEffect, useMemo, useRef } from "react";
import { animate, stagger } from "animejs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ThreadMemberRow } from "@/components/messages/thread-member-row";
import { RightPanelTabs } from "@/components/messages/right-panel-tabs";
import { DirectUserAppointmentsPanel } from "@/components/messages/direct-user-appointments-panel";
// Temporarily hidden along with its usage below.
// import { DirectUserAvailableSlotsPanel } from "@/components/messages/direct-user-available-slots-panel";
import { initialsFromLabel } from "@/lib/messages/messages-helpers";
import { getAvatarColorClasses } from "@/lib/messages/avatar-color";
import { cn } from "@/lib/utils";
import {
  AlertCircle,
  Bot,
  CalendarClock,
  ChevronDown,
  ChevronRight,
  LogOut,
  PanelRightClose,
  PanelRightOpen,
  Phone,
  Stethoscope,
  Users,
  UserSearch,
} from "lucide-react";

const SECTION_KEYS = {
  patients: "patients",
  scheduling: "scheduling",
  team: "team",
  bots: "bots",
};

// Temporarily hide the Team + Bots sections in the participants panel. The data still loads and
// is used elsewhere on the messages page (e.g. the Assign popover reads threadMembersGrouped.team),
// and the section markup below is kept intact — flip this to `true` to bring them back.
const SHOW_TEAM_AND_BOTS_SECTIONS = false;

/**
 * One WhatsApp-group roster entry. Rows come from thread metadata (`metadata.whatsappGroup`),
 * not from backend memberships — roster participants have NO backend userId, so ThreadMemberRow's
 * click-through/details plumbing has nothing to act on and a leaner local row fits better.
 * Identity precedence mirrors the contract: displayName, else phone, else the jid's local part
 * (which for `@s.whatsapp.net` jids IS the phone digits).
 */
function WaGroupParticipantRow({ participant }) {
  const jid = String(participant?.jid || "").trim();
  const phone = String(participant?.phoneNumber || "").trim();
  const label =
    String(participant?.displayName || "").trim() || phone || jid.split("@")[0] || "Unknown";
  const showPhoneLine = Boolean(phone) && phone !== label;
  return (
    <li className="flex items-center gap-3 rounded-lg border border-border/50 bg-background/60 px-3 py-2.5">
      <Avatar className="h-9 w-9 shrink-0">
        <AvatarFallback
          className={cn("text-[11px] font-semibold", getAvatarColorClasses(label))}
        >
          {initialsFromLabel(label)}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="min-w-0 truncate text-sm font-semibold text-foreground" title={label}>
            {label}
          </p>
          {participant?.isAdmin ? (
            <span className="shrink-0 rounded bg-amber-500/15 px-1 py-px text-[9px] font-bold uppercase tracking-wide text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
              Admin
            </span>
          ) : null}
        </div>
        {showPhoneLine ? (
          <p className="mt-0.5 flex min-w-0 items-center gap-1 text-xs tabular-nums text-muted-foreground">
            <Phone className="size-3 shrink-0" strokeWidth={2} aria-hidden />
            <span className="min-w-0 truncate">{phone}</span>
          </p>
        ) : null}
      </div>
    </li>
  );
}

/**
 * Collapsible section header used inside the participants panel. Matches the
 * messages list's section-header look (small uppercase label + chevron).
 */
function ParticipantsSectionHeader({
  sectionKey,
  collapsed,
  onToggle,
  icon: Icon,
  label,
  count,
  className,
  iconClassName,
}) {
  const interactive = typeof onToggle === "function" && Boolean(sectionKey);
  return (
    <button
      type="button"
      onClick={interactive ? () => onToggle(sectionKey) : undefined}
      aria-expanded={!collapsed}
      disabled={!interactive}
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-md px-1 py-1 text-left transition",
        interactive ? "cursor-pointer hover:bg-muted/60" : "cursor-default",
        className,
      )}
    >
      <span className="flex min-w-0 items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {interactive ? (
          collapsed ? (
            <ChevronRight className="h-3 w-3 shrink-0 opacity-70" strokeWidth={2.5} aria-hidden />
          ) : (
            <ChevronDown className="h-3 w-3 shrink-0 opacity-70" strokeWidth={2.5} aria-hidden />
          )
        ) : null}
        {Icon ? <Icon className={cn("h-3.5 w-3.5 shrink-0", iconClassName)} aria-hidden /> : null}
        <span className="truncate">{label}</span>
      </span>
      {typeof count === "number" ? (
        <span
          className={cn(
            "shrink-0 rounded-full bg-muted px-1.5 py-0.5 font-mono text-[10px] font-semibold leading-none text-muted-foreground",
          )}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}

/**
 * @param {object} props
 */
export function ThreadParticipantsPanel({
  isInternalScope = false,
  rightPanelOpen,
  onRightPanelOpenChange,
  threadMembersNotice,
  threadMembersError,
  membersPanelLoading,
  showEmptyMembersPanel,
  memberListSource,
  threadMembersGrouped,
  historyDerivedMemberRows,
  onMemberUserClick,
  collapsedSections,
  onToggleSection,
  // Tabbed right panel (Messages scope): show the Participants ↔ Tasks strip on
  // top of the expanded panel; onShowTasks switches over. The strip hosts the hide-panel
  // control, so the in-content collapse buttons are suppressed while it renders.
  showTasksTab = false,
  onShowTasks,
  tasksBadge = 0,
  // Internal Chats: presence dots on member rows + the group-leave action.
  presenceOnlineIds = null,
  presenceAvailability = null,
  onLeaveGroup,
  // WhatsApp group thread: the normalized roster snapshot (getWaGroupInfo), or null. With a
  // populated roster the panel renders the single "Participants" section from it instead of the
  // membership API sections; a rosterless group falls through to the history-derived rows.
  waGroupInfo = null,
}) {
  const panelRef = useRef(null);

  const directRows = threadMembersGrouped?.direct ?? [];
  const teamRows = threadMembersGrouped?.team ?? [];
  const botRows = threadMembersGrouped?.bots ?? [];

  // Admins first, then alphabetical — the people who can act on the group float to the top.
  const waGroupParticipants = useMemo(() => {
    const list = Array.isArray(waGroupInfo?.participants) ? [...waGroupInfo.participants] : [];
    const rowLabel = (p) =>
      String(p?.displayName || "").trim() ||
      String(p?.phoneNumber || "").trim() ||
      String(p?.jid || "").split("@")[0];
    list.sort((a, b) => {
      const adminDelta = (b?.isAdmin === true ? 1 : 0) - (a?.isAdmin === true ? 1 : 0);
      if (adminDelta !== 0) return adminDelta;
      return rowLabel(a).localeCompare(rowLabel(b), undefined, { sensitivity: "base" });
    });
    return list;
  }, [waGroupInfo]);
  const waGroupMode = waGroupParticipants.length > 0;

  const totalParticipants = waGroupMode
    ? waGroupParticipants.length
    : directRows.length + teamRows.length + botRows.length;
  const primaryDirect = directRows[0] ?? null;

  const collapsed = useMemo(() => {
    return {
      patients: collapsedSections?.has?.(SECTION_KEYS.patients) ?? false,
      scheduling: collapsedSections?.has?.(SECTION_KEYS.scheduling) ?? false,
      team: collapsedSections?.has?.(SECTION_KEYS.team) ?? false,
      bots: collapsedSections?.has?.(SECTION_KEYS.bots) ?? false,
    };
  }, [collapsedSections]);

  useLayoutEffect(() => {
    if (!rightPanelOpen || !panelRef.current) return;
    animate(panelRef.current, {
      translateX: [24, 0],
      opacity: [0, 1],
      duration: 320,
      ease: "outCubic",
    });
    const rows = panelRef.current.querySelectorAll("[data-participant-row]");
    if (rows.length > 0) {
      animate(rows, {
        translateY: [10, 0],
        opacity: [0, 1],
        duration: 320,
        delay: stagger(28),
        ease: "outCubic",
      });
    }
  }, [rightPanelOpen, membersPanelLoading]);

  const isApi = memberListSource === "api";
  // Only the deliberate 403 fallback (source "history") should surface message-derived rows. The
  // previous `!isApi && !error` test also matched the transient "idle"/"loading" states, which made
  // the panel render the message senders (bot + a member) as if they were the real participant list
  // before the members API had resolved — the reported "wrong participants until reload" glitch.
  // "loading" is already masked by the skeleton above; "idle" now renders nothing while the hook
  // self-heals by (re)loading members for the open thread.
  const showHistoryFallback = memberListSource === "history";
  const hasAnyContent =
    isApi
      ? directRows.length + teamRows.length + botRows.length > 0
      : historyDerivedMemberRows.length > 0;

  // Collapse (hide-panel) control. The bulky "Participants" header was removed, so this now sits
  // inline with the Patient/section header. In states that have no such header (loading, empty,
  // error, or a thread with no direct member) we surface it at the top instead — see showTopCollapse —
  // so the panel can always be hidden.
  const hidePanelButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          aria-label="Hide panel"
          aria-controls="messages-thread-right-panel"
          onClick={() => onRightPanelOpenChange(false)}
        >
          <PanelRightClose className="h-4 w-4" strokeWidth={1.75} aria-hidden />
          <span className="sr-only">Hide panel</span>
        </button>
      </TooltipTrigger>
      <TooltipContent side="left" className="text-xs">
        Hide panel
      </TooltipContent>
    </Tooltip>
  );
  // While the tab strip renders it hosts the hide control, so the in-content copy goes away.
  const collapseButton = showTasksTab ? null : hidePanelButton;
  // Mirrors the render branches below: the Patient (api, with rows) and history sections host the
  // collapse button inline; every other state shows it at the top so it is never lost. When the
  // tab strip renders it owns the hide-panel control instead, so both in-content spots go quiet.
  const collapseLivesInTabs = showTasksTab;
  const hasInlineCollapseHost =
    !collapseLivesInTabs &&
    (waGroupMode ||
      (!membersPanelLoading &&
        !showEmptyMembersPanel &&
        (isApi ? directRows.length > 0 : showHistoryFallback)));
  const showTopCollapse = !collapseLivesInTabs && !hasInlineCollapseHost;

  return (
    <TooltipProvider delayDuration={400}>
      {rightPanelOpen ? (
        <aside
          ref={panelRef}
          className="flex h-full min-h-0 w-full flex-col border-l border-border/80 bg-muted/20 shadow-[inset_1px_0_0_0_hsl(var(--border)/0.5)] dark:bg-muted/15"
        >
          {showTasksTab ? (
            <RightPanelTabs
              active="participants"
              tasksBadge={tasksBadge}
              onChange={(tab) => {
                if (tab === "tasks") onShowTasks?.();
              }}
              trailing={hidePanelButton}
            />
          ) : null}
          {/* "Participants" header removed — the collapse control now lives inline with the Patient
              group header (and at the top of the header-less states; see showTopCollapse below). */}
          <ScrollArea id="messages-thread-right-panel" className="min-h-0 flex-1" role="region">
            <div className="space-y-4 px-3 pb-3 pt-3">
              {showTopCollapse ? (
                <div className="flex justify-end">{collapseButton}</div>
              ) : null}

              {threadMembersNotice ? (
                <p className="rounded-md border border-border/40 bg-muted/40 px-3 py-2 text-[11px] text-muted-foreground">
                  {threadMembersNotice}
                </p>
              ) : null}

              {memberListSource === "error" && threadMembersError ? (
                <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                  <p className="min-w-0 flex-1">{threadMembersError}</p>
                </div>
              ) : null}

              {waGroupMode ? (
                // WhatsApp group: one flat roster section straight from thread metadata. Renders
                // ahead of the loading skeleton on purpose — the roster is already in hand, and
                // the members API was never called for this thread (see loadAdminThreadMembers).
                <section className="space-y-1.5" data-participant-row>
                  <div className="flex items-center gap-1">
                    <ParticipantsSectionHeader
                      icon={Users}
                      label="Participants"
                      count={waGroupParticipants.length}
                      className="min-w-0 flex-1"
                    />
                    {collapseButton}
                  </div>
                  <ul className="space-y-1.5">
                    {waGroupParticipants.map((p, idx) => (
                      <WaGroupParticipantRow
                        key={String(p?.jid || p?.phoneNumber || idx)}
                        participant={p}
                      />
                    ))}
                  </ul>
                </section>
              ) : membersPanelLoading ? (
                <div className="space-y-3" data-participant-row>
                  <Skeleton className="h-3 w-24 rounded" />
                  <Skeleton className="h-14 w-full rounded-lg" />
                  <Skeleton className="h-14 w-full rounded-lg" />
                  <Skeleton className="h-3 w-16 rounded" />
                  <Skeleton className="h-9 w-full rounded-md" />
                </div>
              ) : showEmptyMembersPanel ? (
                <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border/60 bg-background/40 px-4 py-8 text-center">
                  <UserSearch className="h-6 w-6 text-muted-foreground/70" aria-hidden />
                  <p className="text-xs font-medium text-foreground">
                    {memberListSource === "history" ? "No senders yet" : "No participants"}
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    {memberListSource === "history"
                      ? "No messages from people we can identify in this thread yet."
                      : "The server returned no members for this thread."}
                  </p>
                </div>
              ) : isApi ? (
                <div className="space-y-4">
                  {directRows.length > 0 ? (
                    <section className="space-y-1.5" data-participant-row>
                      <div className="flex items-center gap-1">
                        <ParticipantsSectionHeader
                          sectionKey={SECTION_KEYS.patients}
                          collapsed={collapsed.patients}
                          onToggle={onToggleSection}
                          icon={isInternalScope ? Users : UserSearch}
                          label={isInternalScope ? "People" : "Patient"}
                          count={directRows.length}
                          className="min-w-0 flex-1"
                        />
                        {collapseButton}
                      </div>
                      {!collapsed.patients ? (
                        <ul className="space-y-1.5">
                          {directRows.map((row) => (
                            <ThreadMemberRow
                              key={row.key}
                              row={row}
                              compact={false}
                              adminDirectClickable={!isInternalScope}
                              onMemberUserClick={isInternalScope ? undefined : onMemberUserClick}
                              presence={
                                presenceOnlineIds instanceof Set && row?.id
                                  ? {
                                      online: presenceOnlineIds.has(String(row.id)),
                                      availability: presenceAvailability?.[String(row.id)],
                                    }
                                  : null
                              }
                            />
                          ))}
                        </ul>
                      ) : null}
                      {!collapsed.patients && onLeaveGroup ? (
                        <button
                          type="button"
                          onClick={onLeaveGroup}
                          className="mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-md border border-destructive/40 px-2 py-1.5 text-xs font-medium text-destructive transition hover:bg-destructive/10"
                        >
                          <LogOut className="h-3.5 w-3.5" aria-hidden />
                          Leave group
                        </button>
                      ) : null}
                    </section>
                  ) : null}

                  {!isInternalScope && primaryDirect ? (
                    <section className="space-y-1.5" data-participant-row>
                      <ParticipantsSectionHeader
                        sectionKey={SECTION_KEYS.scheduling}
                        collapsed={collapsed.scheduling}
                        onToggle={onToggleSection}
                        icon={CalendarClock}
                        label="Scheduling"
                      />
                      {!collapsed.scheduling ? (
                        <ul className="space-y-2">
                          <li>
                            <DirectUserAppointmentsPanel
                              userId={primaryDirect.id}
                              userLabel={primaryDirect.label}
                            />
                          </li>
                          {/* Available Slots panel temporarily hidden — re-enable by uncommenting.
                          <li>
                            <DirectUserAvailableSlotsPanel
                              userId={primaryDirect.id}
                              userLabel={primaryDirect.label}
                            />
                          </li>
                          */}
                        </ul>
                      ) : null}
                    </section>
                  ) : null}

                  {SHOW_TEAM_AND_BOTS_SECTIONS && teamRows.length > 0 ? (
                    <section className="space-y-1.5" data-participant-row>
                      <ParticipantsSectionHeader
                        sectionKey={SECTION_KEYS.team}
                        collapsed={collapsed.team}
                        onToggle={onToggleSection}
                        icon={Stethoscope}
                        label="Team"
                        count={teamRows.length}
                      />
                      {!collapsed.team ? (
                        <ul className="space-y-1.5">
                          {teamRows.map((row) => (
                            <ThreadMemberRow
                              key={row.key}
                              row={row}
                              compact={false}
                              onMemberUserClick={isInternalScope ? undefined : onMemberUserClick}
                            />
                          ))}
                        </ul>
                      ) : null}
                    </section>
                  ) : null}

                  {SHOW_TEAM_AND_BOTS_SECTIONS && botRows.length > 0 ? (
                    <section className="space-y-1.5" data-participant-row>
                      <ParticipantsSectionHeader
                        sectionKey={SECTION_KEYS.bots}
                        collapsed={collapsed.bots}
                        onToggle={onToggleSection}
                        icon={Bot}
                        label="Bots"
                        count={botRows.length}
                        iconClassName="text-emerald-600 dark:text-emerald-400"
                      />
                      {!collapsed.bots ? (
                        <ul className="space-y-1">
                          {botRows.map((row) => (
                            <ThreadMemberRow key={row.key} row={row} compact />
                          ))}
                        </ul>
                      ) : null}
                    </section>
                  ) : null}
                </div>
              ) : showHistoryFallback ? (
                <section className="space-y-1.5" data-participant-row>
                  <div className="flex items-center gap-1">
                    <ParticipantsSectionHeader
                      sectionKey={SECTION_KEYS.patients}
                      collapsed={collapsed.patients}
                      onToggle={onToggleSection}
                      icon={UserSearch}
                      label="From messages"
                      count={historyDerivedMemberRows.length}
                      className="min-w-0 flex-1"
                    />
                    {collapseButton}
                  </div>
                  {!collapsed.patients ? (
                    <ul className="space-y-1.5">
                      {historyDerivedMemberRows.map((row) => (
                        <ThreadMemberRow key={row.key} row={row} compact={false} />
                      ))}
                    </ul>
                  ) : null}
                </section>
              ) : null}

              {hasAnyContent ? null : null /* empty hooks already handled above */}
            </div>
          </ScrollArea>
        </aside>
      ) : (
        <div className="flex h-full min-h-0 w-full flex-col border-l border-border/80 bg-muted/20 shadow-[inset_1px_0_0_0_hsl(var(--border)/0.5)] dark:bg-muted/15">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className="group relative flex w-full flex-col items-center gap-1 border-b border-border/60 bg-background/60 py-3.5 text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                aria-expanded={false}
                aria-controls="messages-thread-right-panel"
                aria-label={
                  totalParticipants > 0
                    ? `Show participants (${totalParticipants})`
                    : "Show participants"
                }
                onClick={() => onRightPanelOpenChange(true)}
              >
                <PanelRightOpen
                  className="h-[18px] w-[18px] shrink-0 opacity-70 transition-opacity group-hover:opacity-100"
                  strokeWidth={1.5}
                  aria-hidden
                />
                {/* {totalParticipants > 0 && !membersPanelLoading ? (
                  <span className="inline-flex min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[9px] font-bold leading-none text-primary-foreground ring-2 ring-background">
                    {totalParticipants > 9 ? "9+" : totalParticipants}
                  </span>
                ) : null} */}
              </button>
            </TooltipTrigger>
            <TooltipContent side="left" className="text-xs">
              {totalParticipants > 0 ? `Show participants · ${totalParticipants}` : "Show participants"}
            </TooltipContent>
          </Tooltip>
        </div>
      )}
    </TooltipProvider>
  );
}
